import * as React from 'react';
import { useNavigate } from 'react-router-dom';

//MUI
import PropTypes, { InferProps } from 'prop-types';
import {
  Box,
  Typography,
  useTheme,
  Menu,
  IconButton,
  Badge,
  Stack,
} from '@mui/material';
import {
  CustomAppBar,
  NavIcon,
  ProfileName,
  ProfileAvatar,
  CustomBadge,
  CustomBadgeDot,
  ShadowBox,
} from 'theme/layouts/navbar';
//reducers
import { getCompanies, getTerms, getUserDetails } from 'state/reducers/user.reducers';
import { useAppSelector } from 'hooks/redux.hooks';
import NotificationsBox from 'components/Notifications/NotificationsBox';
import { getNotifications } from 'state/reducers/notifications.reducer';
import { getMobile } from 'state/reducers/ui.reducers';
import { DialogCard } from 'theme/elements/Cards';
import Profile from './Profile';
import { getDir, selectImageUrl, selectLanguage } from 'state/reducers/translation.reducers';
import { pagesEnum } from 'constants/ui.constants';
import { getCompanyLetter, getCompanyName } from 'utils';

DashboardNavbar.propTypes = {
  open: PropTypes.bool.isRequired,
  toggleSidebar: PropTypes.func.isRequired,
  toggleMobileSidebar: PropTypes.func.isRequired,
  setAnchorEl: PropTypes.func.isRequired,
  anchorEl: PropTypes.any,
  nosidebar: PropTypes.bool,
};

export default function DashboardNavbar({
  anchorEl,
  setAnchorEl,
  toggleMobileSidebar,
  open,
  nosidebar,
}: InferProps<typeof DashboardNavbar.propTypes>) {
  const user = useAppSelector(getUserDetails);
  const theme = useTheme();
  const companies = useAppSelector(getCompanies);
  const imageUrl = useAppSelector(selectImageUrl);
  const openMenu = Boolean(anchorEl);
  const unreadAmount = useAppSelector(getNotifications)?.filter((notification) => !notification.readBy).length;
  const [notificationsAnchorEl, setNotificationsAnchorEl] = React.useState<null | HTMLElement>(null);
  const openNotifications = Boolean(notificationsAnchorEl);
  const isMobile = useAppSelector(getMobile);
  const navigate = useNavigate();
  const selectedLang = useAppSelector(selectLanguage);
  const dir = useAppSelector(getDir);
  const agreedTerms = useAppSelector(getTerms);

  const notificationsClick = (event: any) => {
    setNotificationsAnchorEl(event.currentTarget);
  };
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (action?: string) => {
    setAnchorEl(null);
    setNotificationsAnchorEl(null);
  };

  return (
    <CustomAppBar open={open} nosidebar={nosidebar} dir={dir}>
      <Box
        sx={{
          flexGrow: 1,
          [theme.breakpoints.down('sm')]: {
            display: 'flex',
            alignItems: 'center',
          },
        }}
      >
        {isMobile && !nosidebar && (
          <IconButton onClick={toggleMobileSidebar}>
            <img src={imageUrl('navbar.menu_button.icon') || '/icons/menu.svg'} />
          </IconButton>
        )}
        <img
          onClick={() => {
            navigate(`/${pagesEnum.DASHBOARD}`);
          }}
          src={imageUrl('navbar.header.logo.icon') || '/static_images/brand-logo-color.webp'}
          alt="open"
          style={{ cursor: 'pointer', maxHeight: '50px' }}
        />
      </Box>
      {agreedTerms && <NavIcon onClick={(e) => notificationsClick(e)}>
        <ShadowBox open={openNotifications}>
          <CustomBadge>
            <CustomBadgeDot>
              <Badge badgeContent={unreadAmount}>
                <img src={imageUrl('navbar.notifications_button.icon') || '/icons/bell.svg'} />
              </Badge>
            </CustomBadgeDot>
          </CustomBadge>
        </ShadowBox>
      </NavIcon>}
      {isMobile ? (
        <DialogCard open={openNotifications} handleClose={handleClose}>
          <NotificationsBox handleClose={handleClose} unreadAmount={unreadAmount} />
        </DialogCard>
      ) : (
        <Menu
          id="basic-menu"
          anchorEl={notificationsAnchorEl}
          open={openNotifications}
          onClose={() => handleClose()}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
        >
          <NotificationsBox handleClose={handleClose} unreadAmount={unreadAmount} />
        </Menu>
      )}

      <ProfileName className="desktop-item">
        <Stack>
          <Typography variant="boldBody18" color={theme.palette.root.basic5}>
            {getCompanyName(user?.companyHebrewName, user?.companyName, selectedLang)}
          </Typography>
          <Typography variant="body3">{user?.companyAccountId}</Typography>
        </Stack>
        <Box></Box>
      </ProfileName>
      <IconButton
        className="desktop-item"
        id="basic-button"
        aria-controls={openMenu ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={openMenu ? 'true' : undefined}
        onClick={handleClick}
      >
        <ProfileAvatar>
          {getCompanyLetter(user?.companyHebrewName, user?.companyName, selectedLang)}
        </ProfileAvatar>
      </IconButton>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={openMenu}
        onClose={() => handleClose()}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <Profile handleClose={handleClose} companies={companies} />
      </Menu>
    </CustomAppBar>
  );
}
